import Text from 'components/_UI/Text';
import { useCheckout } from 'context/checkout/Checkout';
import currency from 'helpers/currency';
import React, { useState, useEffect } from 'react';
import { Minus, MinusCircle, MinusSquare, Plus, PlusCircle, PlusSquare } from 'lucide-react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import date from 'helpers/date';
import { FullyCentered } from 'styled/alignment/Center';
import fonts from 'theme/fonts';

export default function TicketItem({ ticket, type, soldout, finished, saleType, minimal = false, loginModalCount = false, selectedTickets, description, setSelectedTickets, couponTrigger, onChangeTotalValue }) {
    const [quantity, setQuantity] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);

    const { checkout, setCheckout } = useCheckout();

    // Função para calcular a quantidade de itens selecionados por id
    const calculateQuantityFromSelectedTickets = (id) => {
        return selectedTickets.filter(selectedTicket => selectedTicket.id === id).length;
    };

    const incrementQuantity = () => {
        if (quantity < ticket.quantity && quantity < 10) {
            setQuantity(prev => prev + 1);

            const updatedTotalPrice = ticket.price * (quantity + 1);
            const updatedTotalOriginalPrice = Number(ticket.original_price) * (quantity + 1);
            const priceChange = updatedTotalPrice - totalPrice;
            const totalPriceChange = updatedTotalOriginalPrice - (Number(ticket.original_price) * quantity);

            setTotalPrice(updatedTotalPrice);
            onChangeTotalValue(priceChange, totalPriceChange);

            // Adiciona uma nova instância do ticket completo ao selectedTickets
            setSelectedTickets(prevSelectedTickets => [
                ...prevSelectedTickets,
                {
                    ...ticket,
                    type
                }
            ]);
        }
    };

    const decrementQuantity = () => {
        if (quantity > 0) {
            setQuantity(prev => prev - 1);

            const updatedTotalPrice = ticket.price * (quantity - 1);
            const updatedTotalOriginalPrice = Number(ticket.original_price) * (quantity - 1);
            const priceChange = updatedTotalPrice - totalPrice;
            const totalPriceChange = updatedTotalOriginalPrice - (Number(ticket.original_price) * quantity);

            setTotalPrice(updatedTotalPrice);
            onChangeTotalValue(priceChange, totalPriceChange);

            // Remove apenas uma instância do ticket correspondente
            setSelectedTickets(prevSelectedTickets => {
                const ticketIndex = prevSelectedTickets.findIndex(selectedTicket => selectedTicket.id === ticket.id);
                if (ticketIndex > -1) {
                    const updatedTickets = [...prevSelectedTickets];
                    updatedTickets.splice(ticketIndex, 1); // Remove uma instância do ingresso
                    return updatedTickets;
                }
                return prevSelectedTickets;
            });
        }
    };

    useEffect(() => {
        if (loginModalCount > 1) {
            setQuantity(0);
            setTotalPrice(0);
        }

        if (quantity === 0) {
            setCheckout(prevCheckout => ({
                ...prevCheckout,
                batches: prevCheckout?.batches?.filter(item => item.id !== ticket.id) || []
            }));
        } else {
            setCheckout(prevCheckout => {
                const existingBatch = prevCheckout?.batches?.find(item => item.id === ticket.id);
                if (existingBatch) {
                    // Atualiza a quantidade do item existente
                    return {
                        ...prevCheckout,
                        batches: prevCheckout.batches.map(item =>
                            item.id === ticket.id
                                ? { ...item, quantity: quantity }
                                : item
                        )
                    };
                } else {
                    return {
                        ...prevCheckout,
                        batches: [...prevCheckout.batches, { id: ticket.id, quantity: quantity }]
                    };
                }
            });
        }
    }, [quantity, loginModalCount]);

    // useEffect para lidar com o couponTrigger
    useEffect(() => {
        // Atualiza a quantidade conforme o selectedTickets
        const newQuantity = calculateQuantityFromSelectedTickets(ticket.id);
        setQuantity(newQuantity);

        const updatedTotalPrice = ticket.price * newQuantity;
        const updatedTotalOriginalPrice = Number(ticket.original_price) * newQuantity;
        const priceChange = updatedTotalPrice - totalPrice;
        const totalPriceChange = updatedTotalOriginalPrice - totalPrice;

        setTotalPrice(updatedTotalPrice);
        onChangeTotalValue(priceChange, totalPriceChange);
    }, [couponTrigger]);

    return (
        <>
            {
                (!!ticket?.is_active && ticket?.quantity > 0) &&(
                    <TicketItemContainer minimal={minimal} >
                        <Column>
                            <Text type='bold' marginBottom={'5px'} color={colors.nightDark}>
                                {ticket.name}
                            </Text>

                            {
                                (ticket?.description || description) && (
                                    <ContentTextDescription>
                                        <Text
                                            color={colors.night}
                                            marginBottom={'5px'}
                                            marginTop={'-6px'}
                                            name='small'
                                            paddingRight={'25px'}
                                        >
                                            {ticket?.description || description}
                                        </Text>
                                    </ContentTextDescription>
                                )
                            }

                            <Column width='80%'>
                                {
                                    Number(ticket?.original_price) === Number(ticket?.price) &&
                                    <Text color={colors.night} marginBottom={'5px'}>
                                        {ticket?.original_price != 0 ? currency.format(ticket?.original_price) : 'Gratuito'}
                                    </Text>
                                }
                                {
                                    Number(ticket?.original_price) !== Number(ticket?.price) &&
                                    <Row marginBottom={'5px'}>
                                        <Text
                                            color={colors.nightLight}
                                            marginRight={'.5rem'}
                                            decoration={'line-through'}
                                        >
                                            {currency.format(ticket?.original_price)}
                                        </Text>
                                        <Text color={colors.success}>
                                            {currency.format(ticket?.price)}
                                        </Text>
                                    </Row>
                                }

                                {
                                    (ticket.sale_end && saleType == 'sale_period') &&
                                    <Text color={colors.night} name='small' type='italic' paddingBottom={'4px'}>
                                        Vendas até {date.format(ticket.sale_end)}
                                    </Text>

                                }

                                {
                                    (ticket.quantity <= 15 && !soldout && !finished) &&
                                    <LastUnits>
                                        <Text
                                            color={colors.occasionalOrange}
                                            name='small'
                                            paddingBottom={0}
                                        >
                                            Restam poucos ingressos
                                        </Text>
                                    </LastUnits>
                                }
                            </Column>
                        </Column>

                        <QuantityContainer>
                            {
                                !soldout && (
                                    <>
                                        <ToggleValuesContainer fullyCentralized>
                                            <ActionContainer
                                                minus
                                                disabled={quantity === 0}
                                                onClick={decrementQuantity}
                                            >
                                                <Minus
                                                    style={{ cursor: `${quantity === 0 ? 'not-allowed' : 'pointer'}` }}
                                                    color={quantity === 0 ? colors.night : colors.neutralLight}
                                                    size={20}
                                                />
                                            </ActionContainer>

                                            <Text marginLeft={'6px'} marginRight={'6px'} color={colors.night} paddingBottom={0}>
                                                {quantity}
                                            </Text>

                                            <ActionContainer
                                                add
                                                disabled={quantity === 10 || quantity === ticket?.quantity}
                                                onClick={incrementQuantity}
                                            >
                                                <Plus
                                                    color={quantity === 10 || quantity === ticket?.quantity ? colors.nightLight : colors.neutralLight}
                                                    size={20}
                                                />
                                            </ActionContainer>
                                        </ToggleValuesContainer>
                                        <Text type='bold' color={colors.night} paddingBottom={0}>
                                            R$ {totalPrice.toFixed(2)}
                                        </Text>
                                    </>
                                )
                            }

                        </QuantityContainer>
                    </TicketItemContainer >
                )
            }

            {
                (soldout || finished) && (
                    <TicketItemContainer minimal={minimal} >
                        <Column>
                            <Text type='bold' marginBottom={'5px'} color={colors.nightDark}>
                                {ticket.name}
                            </Text>

                            {
                                (ticket?.description || description) && (
                                    <ContentTextDescription>
                                        <Text
                                            color={colors.night}
                                            marginBottom={'5px'}
                                            marginTop={'-6px'}
                                            name='small'
                                            paddingRight={'25px'}
                                        >
                                            {ticket?.description || description}
                                        </Text>
                                    </ContentTextDescription>
                                )
                            }

                            <Column width='80%'>
                                {
                                    Number(ticket?.original_price) === Number(ticket?.price) &&
                                    <Text color={colors.night} marginBottom={'5px'}>
                                        {ticket?.original_price != 0 ? currency.format(ticket?.original_price) : 'Gratuito'}
                                    </Text>
                                }
                                {
                                    Number(ticket?.original_price) !== Number(ticket?.price) &&
                                    <Row marginBottom={'5px'}>
                                        <Text
                                            color={colors.nightLight}
                                            marginRight={'.5rem'}
                                            decoration={'line-through'}
                                        >
                                            {currency.format(ticket?.original_price)}
                                        </Text>
                                        <Text color={colors.success}>
                                            {currency.format(ticket?.price)}
                                        </Text>
                                    </Row>
                                }
                            </Column>
                        </Column>

                        <QuantityContainer>
                            {
                                (soldout || ticket?.quantity == 0) && (
                                    <Text marginLeft={'6px'} marginRight={'6px'} color={colors.night} paddingBottom={0} type='bold'>
                                        Esgotado
                                    </Text>
                                )
                            }

                            {
                                !soldout && finished && (
                                    <Text marginLeft={'6px'} marginRight={'6px'} color={colors.dangerLight} paddingBottom={0} type='bold'>
                                        Encerrado
                                    </Text>
                                )
                            }

                        </QuantityContainer>
                    </TicketItemContainer >
                )
            }
        </>
    )
    // : (
    //     <Column marginTop='15px'>
    //         <Text textAlign={'center'} color={colors.night} type='bold' name={fonts.sizes.default}>
    //             Ingressos pausados pelo produtor.
    //         </Text>
    //     </Column>
    // )
}

const TicketItemContainer = styled(Row)`
    margin-top: 20px;
    /* padding: 14px 0; */
    align-items: center;
    /* background-color: ${colors.neutral}; */
    border-radius: 6px;
    margin-top: 14px;
    padding: 14px;
    border: 1px solid ${colors.neutralDark};
    box-shadow: 0 0 3px #ccc;
    /* border-bottom: 1px solid ${colors.nightLight}; */

    /* ${({ stoped }) => stoped && `
        opacity: .65;
    `}; */
    
    /* ${({ soldout }) => soldout && `
        opacity: .7;
    `}; */

    /* @media screen and (min-width: ${breakpoints.lg}) {
        border-bottom: none;
        /* border-top: 1px solid ${colors.nightLight}; */
        /* margin-top: 0; */
        
        /* &:first-child {
            border-top: none;
        } */

        /* ${({ minimal }) => minimal && `
            border-top: none;
            border-bottom: 1px solid ${colors.nightLight};
        `} */
    /* } */ 
`;

const LastUnits = styled(FullyCentered)`
    border: 1px solid ${colors.occasionalOrange};
    border-radius: 30px;
    width: 162px;
    padding: 5px;
    background-color: #fcf7eb;
`;

const QuantityContainer = styled(Column)`
    flex-direction: column;
    width: auto;
    align-items: center;
    width: 100px;
`;

const ToggleValuesContainer = styled(Row)`
    margin-bottom: 10px;
    width: auto;
    /* border: 1px solid ${colors.nightLight};
    border-radius: 6px; */
    /* padding: 6px; */
`;

const ActionContainer = styled(FullyCentered)`
    width: 30px;
    height: 30px;
    background: ${colors.occasionalPurple};
    border-radius: 6px;
    cursor: pointer;
    transition: all ease-in-out .25s;

    
    ${({ add }) => add && `
        margin-left: 4px;
        
        &:hover { 
            opacity: .8;
        }
    `}
    
    ${({ minus }) => minus && `
        margin-right: 4px;

        &:hover { 
            opacity: .8;
        }
    `}

    ${({ disabled }) => disabled && `
        background: ${colors.neutralLight};
        border: 1px solid ${colors.night};
        pointer-events: none;
    `}
`;


const ContentTextDescription = styled.div`
    & > p {
        word-break: keep-all;
    }
`
